<template>
  <section :class="$style.card">
    <header :class="$style.header">
      <ui-avatar
        :src="account?.avatar"
        :border="account?.styles?.avatarBorderColor"
        :sticker="account?.styles?.sticker"
        :class="$style.avatar"
        size="m"
      />
      <div :class="$style.username">{{ username || t('guest') }}</div>
      <ui-link :class="$style.profile" :to="'/profile/' + account.id">
        <span>{{ t('profile') }}</span>
        <ui-icon name="chevron-right" :class="$style.profileIcon" />
      </ui-link>
    </header>

    <dl :class="$style.stats">
      <div :class="$style.stat">
        <dd :class="$style.statValue">
          <ui-icon name="xp" :class="$style.statIcon" />
          <span>{{ userSummary.xp }}</span>
        </dd>
        <dt :class="$style.statLabel">{{ t('stats.totalXp') }}</dt>
      </div>

      <div :class="$style.stat">
        <dd :class="$style.statValue">
          <ui-icon name="darts" :class="$style.statIcon" />
          <span>{{ userSummary.correctCount }}</span>
        </dd>
        <dt :class="$style.statLabel">{{ t('stats.correct') }}</dt>
      </div>

      <div :class="$style.stat" v-if="me">
        <dd :class="$style.statValue">
          <ui-icon name="trophy" :class="$style.statIcon" />
          <span>{{ me.position }}</span>
        </dd>
        <dt :class="$style.statLabel">{{ t('stats.ratingPlace') }}</dt>
      </div>

      <div :class="$style.stat">
        <button :class="$style.leaders" @click="emit('open-leaderboard')">
          <ui-icon name="trophy" :class="$style.leadersIcon" />
          <span>{{ t('leaders') }}</span>
        </button>
      </div>
    </dl>
  </section>
</template>

<i18n lang="yaml">
ru:
  guest: Гость
  subtitle: Pick'em
  profile: Профиль
  leaders: Лидеры
  stats:
    totalXp: Опыта
    correct: Угадано
    ratingPlace: В рейтинге
</i18n>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import UiAvatar from '~/components/base/avatar/index.vue'
import { useAccountStore } from '~/stores/account'
import { useTournamentStore } from '../../stores/tournament'
import { useLeaderboardStore } from '../../stores/leaderboard'
import UiIcon from '~/ui-core/components/icon/index.vue'
import UiLink from '~/components/base/link/index.vue'

defineOptions({ name: 'PickemPlayerStatsCard' })

const emit = defineEmits<{
  'open-leaderboard': []
}>()

const { t } = useI18n()

const accountStore = useAccountStore()
const { account } = storeToRefs(accountStore)

const tournamentStore = useTournamentStore()
const leaderBoardStore = useLeaderboardStore()
const { userSummary, tournament } = storeToRefs(tournamentStore)
const { me } = storeToRefs(leaderBoardStore)

const tournamentId = computed(() => tournament.value?.id)

watch(
  tournamentId,
  () => {
    if (tournamentId.value) leaderBoardStore.fetchMe(tournamentId.value)
  },
  { immediate: true }
)

const username = computed(() => account.value?.username ?? '')
</script>

<style lang="scss" module>
.card {
  display: flex;
  flex-direction: column;
  gap: 1.6em;
  padding: 2em;
  border-radius: 1.2em;
  background-color: color-get(--root-rgb-bg-card);
}

.header {
  display: flex;
  align-items: center;
  gap: 1.2em;
}

.avatar {
  width: 2.4em;
  height: 2.4em;
  flex-shrink: 0;
}

.username {
  @include typo-get(subhead-l);
  color: color-get(--root-rgb-text-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile {
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 0.3em;

  @include typo-get(label-m);
  color: color-get(--root-rgb-accent-2);
}

.profileIcon {
  width: 1.2em;
  height: 1.2em;
}

.stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 1.2em 0;
  margin: 0;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  min-width: 0;
}

.statLabel {
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-2);
}

.statIcon {
  width: 1em;
  color: color-get(--root-rgb-text-2);
}

.statValue {
  display: flex;
  align-items: center;
  gap: 0.4em;
  @include typo-get(subhead-m);
  color: color-get(--root-rgb-text-1);
}

.leaders {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6em;
  background: color-get(--root-rgb-control-default-bg);
  @include typo-get(label-m);
  padding: 0.8em 2.4em;
  border-radius: 0.3em;
  border: 1px solid color-get(--root-rgb-control-default-bg);
  transition: background 0.15s ease;

  @include hover {
    background: color-get(--root-rgb-bg-special-2);
  }
}

.leadersIcon {
  flex-shrink: 0;
  width: 1em;
  color: color-get(--root-rgb-text-2);
}
</style>
